import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { MoreVertical } from 'lucide-react'
import { useState } from 'react'
import type { RCard } from 'v2/types/api/billing'
import { useUpdateDefaultCardMutation } from '../mutations'
import { canDecideDelete, canMakeDefault, isAutopayCard } from './card-eligibility'
import { DeleteCardDialog } from './delete-card-dialog'
import { useCardDeleteRouting } from './use-card-delete-routing'
import { useCardEligibility } from './use-card-eligibility'

// 13px / 400 / 100% for every row in this menu.
const MENU_ITEM_TEXT_CLASS = 'text-s-400 leading-none'

interface CardRowActionsProps {
  card: RCard
  disabled?: boolean
  /**
   * Takes over "Remove card" for cases that are NOT about which PSP owns the card
   * — currently only the autopay card, which has to route via the confirm dialog
   * because deleting it would silently stop auto-recharge.
   *
   * PSP routing is NOT a reason to pass this: `useCardDeleteRouting` already sends
   * a Razorpay token down the token route wherever this menu is rendered. Passing
   * an override here opts that out, so an override must handle it itself.
   */
  onDelete?: (card: RCard) => void
  /** External in-flight state, for when the delete runs through a page-level mutation. */
  deleting?: boolean
}

/**
 * Per-card overflow menu for the card picker: make default / remove.
 *
 * Rendered as a sibling of the row's menu item rather than inside it — a button
 * nested in a `DropdownMenuItem` still fires the item's `onSelect`, so opening
 * this menu would also pick the card and close the picker underneath it.
 */
export const CardRowActions = ({
  card,
  disabled = false,
  onDelete,
  deleting = false,
}: CardRowActionsProps) => {
  const { mutate: updateDefaultCard, isPending: isUpdatingDefault } = useUpdateDefaultCardMutation()
  const { deleteCardForPsp, isDeleting: internalDeleting } = useCardDeleteRouting()
  const { context, otherActiveCards } = useCardEligibility(card)
  const [confirmingAutopayDelete, setConfirmingAutopayDelete] = useState(false)
  const isDeleting = internalDeleting || deleting
  const isBusy = disabled || isUpdatingDefault || isDeleting

  const handleRemove = () => {
    if (onDelete) {
      onDelete(card)
      return
    }
    // Deleting the card that backs auto-recharge turns auto-recharge off, so it
    // goes through the confirm dialog — which offers a replacement card and says
    // what will happen — instead of the plain delete. This lives here rather than
    // at the call site because the picker in the recharge modals exposes the same
    // Remove action and had no such guard.
    if (isAutopayCard(card, context)) {
      setConfirmingAutopayDelete(true)
      return
    }
    deleteCardForPsp(card)
  }

  const handleMakeDefault = () => {
    updateDefaultCard({ card_mapping_id: card.card_mapping_id })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isBusy}>
          <Button
            type="button"
            variant="ghost"
            className="size-7 shrink-0 p-0 hover:bg-hv1"
            data-testid={`card-actions-trigger-${card.card_mapping_id}`}
            aria-label={`Actions for card ending ${card.last_four_digits}`}
          >
            <MoreVertical className="size-4 text-secondary" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          <DropdownMenuItem
            // 13px / 400 / 100%. DropdownMenuItem ships stock `text-sm` (14px);
            // `cn` knows the house font-size group, so the token replaces it.
            // Both items carry it — one menu with two type sizes reads as a bug.
            className={MENU_ITEM_TEXT_CLASS}
            // Inert rather than absent for an ineligible card, so the menu's shape
            // doesn't change between rows. Ineligible means already the default,
            // or not ACTIVE: an expired or authentication-required card cannot be
            // charged, so promoting it would point billing at a card that fails.
            disabled={!canMakeDefault(card)}
            onSelect={handleMakeDefault}
            data-testid={`card-actions-make-default-${card.card_mapping_id}`}
          >
            Make this the default card for payments
          </DropdownMenuItem>
          <DropdownMenuItem
            className={cn(MENU_ITEM_TEXT_CLASS, 'text-destructive focus:text-destructive')}
            // Until the account/balance queries settle we cannot tell whether this
            // is the autopay card, and guessing wrong deletes it without the
            // safeguard — so hold the action rather than risk it.
            disabled={!onDelete && !canDecideDelete(context)}
            onSelect={handleRemove}
            data-testid={`card-actions-remove-${card.card_mapping_id}`}
          >
            Remove card
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteCardDialog
        key={card.card_mapping_id}
        isOpen={confirmingAutopayDelete}
        onClose={() => setConfirmingAutopayDelete(false)}
        card={confirmingAutopayDelete ? card : null}
        otherActiveCards={otherActiveCards}
      />
    </>
  )
}
